const DEFAULT_NUMBER_FMT = new Intl.NumberFormat('en-US');

/** What an unknown value reads as. Never blank — a blank cell looks like a
    rendering bug, and never 0, which looks like a fact. */
export const NA = 'N/A';

export const formatNumber = (value: number | string | null | undefined, options?: Intl.NumberFormatOptions): string => {
    if (value == null || value === '') return NA;
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(n)) return NA;
    return options ? new Intl.NumberFormat('en-US', options).format(n) : DEFAULT_NUMBER_FMT.format(n);
};

export const formatCurrency = (value: number | null | undefined, decimals = 2): string => {
    if (value == null || Number.isNaN(value)) return NA;
    return formatNumber(value, { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const formatPercent = (value: number | null | undefined, decimals = 2): string => {
    if (value == null || Number.isNaN(value)) return NA;
    return `${value.toFixed(decimals)}%`;
};

/** Formats a plain yyyy-mm-dd day. Parsed field-by-field rather than through
    `new Date(str)`, which reads a bare date as UTC midnight and so renders as
    the previous day for anyone west of Greenwich. */
export const formatDay = (ymd: string | null | undefined): string => {
    if (!ymd) return NA;
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return NA;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Formats a plain yyyy-mm bucket as a month. Field-by-field for the same reason
    formatDay is: a bare string is read as UTC midnight and renders as the month
    before for anyone west of Greenwich. */
export const formatMonth = (ym: string | null | undefined): string => {
    if (!ym) return NA;
    const [y, m] = ym.split('-').map(Number);
    if (!y || !m) return NA;
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
};

/** Today as yyyy-mm-dd in the local timezone, for `<input type="date">`. */
export const todayISO = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Parses a free-typed numeric input. Empty / unparseable reads back as null so
// callers can decide the fallback rather than silently getting 0.
export const parseNumber = (raw: string): number | null => {
    const cleaned = raw.replace(/[$,%\s,]/g, '');
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
};

/** Compact magnitude: 1_773_800_000 -> "1.77B". Null reads as N/A. */
export const roundLargeNumber = (value: number | null | undefined): string => {
    if (value == null || !Number.isFinite(value)) return NA;
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
    return `${sign}${abs}`;
};

/** "$1,234.00/yr", or a bare N/A — never "N/A/yr". */
export const perYear = (value: number | null | undefined): string => (value == null ? NA : `${formatCurrency(value)}/yr`);

/** The same figure a month at a time. This is a book built to be lived off, so
    "what does it pay me this month" is the question actually being asked. */
export const perMonth = (annual: number | null | undefined): string => (annual == null ? NA : `${formatCurrency(annual / 12)}/mo`);

/**
 * Sums values that may be unknown. Returns null if any one of them is, rather
 * than a total that silently omits what it could not price — a partial sum
 * presented as a total is worse than an honest N/A.
 */
export const sumOrNull = (values: (number | null)[]): number | null => (values.some((v) => v == null) ? null : values.reduce((sum, v) => sum! + v!, 0));

/**
 * An exchange's code rather than its full name.
 *
 * Issuers publish "Toronto Stock Exchange (TSX)", which spends most of a column
 * to say TSX. The parenthetical is the code wherever there is one; the only
 * value in the data without one is mapped by hand, and anything unrecognised
 * falls through unchanged rather than being truncated into something wrong.
 */
const EXCHANGE_SHORT: Record<string, string> = { 'cboe canada': 'Cboe' };

export const exchangeCode = (exchange: string | null | undefined): string | null => {
    if (exchange == null) return null;
    const code = exchange.match(/\(([^)]+)\)\s*$/);
    if (code) return code[1].trim();
    return EXCHANGE_SHORT[exchange.trim().toLowerCase()] ?? exchange;
};
